import { ActionContext, ActionTree, MutationTree, GetterTree } from 'vuex'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import { AxiosPromise, AxiosResponse } from 'axios'
import {
  AUTH__CHECK,
  AUTH__FORGOT,
  AUTH__INVITATION,
  AUTH__LOGIN,
  AUTH__LOGOUT,
  AUTH__RESET,
  AUTH__RECOVER,
  AUTH__VERIFY_CODE,
  AUTH__REGISTER_CONFIRM,
  AUTH__REGISTER_REQUEST,
  SETTINGS__INIT,
} from '../actions'
import { CompanyActionTypes } from './companies'
import { _AUTH__SAVE_AUTH, _AUTH__SET_EMAIL } from '../mutations'
import { UserMutationTypes } from '@/store/modules/users/mutations'
import { ActiveScriptMutationTypes } from '@/store/modules/active-script/mutations'
import api from '@/services/api'
import router, { RouteNames } from '@/router'
import { sendAnalyticEvent, AnalyticsEvent } from '@/helpers/analytics'
import { localStorageGetMap, localStorageSet } from '@/helpers/localStorage'

// Types definitions

export const enum InvitationStatus {
  None = 0,
  GuestRegister, // Required user registration
  OtherRegister, // Required user registration, but now logged in other user
  AcceptedLogin, // User already registered, required login
  Accepted, // Invitation accepted
  AcceptedOtherLogin, // Invitation accepted, but now logged in other user
}

export interface InvitationPayload {
  status: InvitationStatus
  key?: string
  token?: string
  expires?: number
}

export interface AuthState {
  email: string
  token: string
  expires: number
}

export interface RegisterPayload {
  fname: string
  lname: string
  password: string
  key: string
}

interface RegisterResponse {
  token: string
  expires: number
  userId: string
  companyId?: string
}

interface LoginPayload {
  email: string
  password: string
  key?: string
}

interface LoginResponse {
  token: string
  expires: number
}

export interface AuthRecoveryPayload {
  key: string
  password: string
}

type AuthContext = ActionContext<AuthState, unknown>

// Store module

function initialState(): AuthState {
  const { email, token, expires } = localStorageGetMap(
    'email',
    'token',
    'expires',
  )
  const expiredNumber = expires ? parseInt(expires) : 0
  return {
    email,
    token,
    expires: expiredNumber,
  }
}

const state: AuthState = initialState()

const getters: GetterTree<AuthState, unknown> = {
  isLoggedIn: (state: AuthState): boolean => {
    const now = +new Date()
    return state.token !== '' && state.expires > now
  },
}

const actions: ActionTree<AuthState, unknown> = {
  [AUTH__CHECK](context: AuthContext, key: string): AxiosPromise<boolean> {
    const url = `/access/verify?key=${key}`
    return api.get(url)
  },

  [AUTH__INVITATION](
    { dispatch, commit }: AuthContext,
    key: string,
  ): AxiosPromise<InvitationPayload> {
    const url = `/access/invitation?key=${key}`
    return api
      .get(url)
      .then(async (response: AxiosResponse<InvitationPayload>) => {
        if (response.data.status === InvitationStatus.AcceptedLogin) {
          commit(_AUTH__SAVE_AUTH, {
            token: response.data.token,
            expires: response.data.expires,
          } as LoginResponse)
        }

        if (
          [InvitationStatus.Accepted, InvitationStatus.AcceptedLogin].includes(
            response.data.status,
          )
        ) {
          await dispatch(CompanyActionTypes.FetchAndActivateNew)
        }
        return response
      })
  },

  [AUTH__LOGIN](
    { commit, dispatch, state, rootGetters }: AuthContext,
    password: string,
  ): AxiosPromise<LoginResponse> {
    const payload: LoginPayload = {
      password,
      email: state.email,
    }
    return api.post('/access/auth', payload).then(async response => {
      commit(_AUTH__SAVE_AUTH, response.data)
      await dispatch(SETTINGS__INIT)
      sendAnalyticEvent(AnalyticsEvent.LOGIN, { user: rootGetters.getMe })
      return response
    })
  },

  [AUTH__LOGOUT]({
    dispatch,
    rootGetters,
  }: AuthContext): AxiosPromise<boolean> {
    sendAnalyticEvent(AnalyticsEvent.LOGOUT, { user: rootGetters.getMe })
    return api.get('/access/logout').finally(() => {
      dispatch(AUTH__RESET)
      dispatch(ADD_TOAST_MESSAGE, {
        text: 'Вы успешно вышли из системы',
      })
    })
  },

  [AUTH__RESET]({ commit }: AuthContext): void {
    commit(_AUTH__SAVE_AUTH, { token: '', expires: 0 })
    commit(UserMutationTypes.SetActiveCompany, '')
    commit(ActiveScriptMutationTypes.Finish)
    router.replace({ name: RouteNames.Login })
  },

  [AUTH__REGISTER_CONFIRM](
    { commit, dispatch, rootGetters }: AuthContext,
    payload: RegisterPayload,
  ): AxiosPromise<RegisterResponse> {
    return api
      .post<RegisterResponse>('/access/register', payload)
      .then(async response => {
        commit(_AUTH__SAVE_AUTH, response.data)
        await dispatch(SETTINGS__INIT)
        if (response.data.companyId) {
          sendAnalyticEvent(AnalyticsEvent.REGISTER_SUCCESS, {
            user: rootGetters.getMe,
          })
        }
        return response
      })
  },

  [AUTH__REGISTER_REQUEST]({ state }: AuthContext): AxiosPromise<boolean> {
    const data = {
      email: state.email,
      language: 'ru',
    }
    return api.post('/access/request', data).then(response => {
      sendAnalyticEvent(AnalyticsEvent.REGISTER_START, {
        user: { email: state.email },
      })
      return response
    })
  },

  [AUTH__FORGOT]({ state }: AuthContext): AxiosPromise<unknown> {
    const payload = {
      email: state.email,
    }
    return api.post('/access/forgot', payload)
  },

  [AUTH__RECOVER](
    context: AuthContext,
    payload: AuthRecoveryPayload,
  ): AxiosPromise<boolean> {
    return api.post('/access/recover', payload)
  },

  async [AUTH__VERIFY_CODE](
    context: AuthContext,
    payload: string,
  ): Promise<boolean> {
    const { data } = await api.get<boolean>(
      `/access/verify?key=${payload}&type=recovery`,
    )
    return data
  },
}

const mutations: MutationTree<AuthState> = {
  /**
   * Saves user data after authorization
   *
   * @param {AuthState} state
   * @param {LoginResponse} payload
   */
  [_AUTH__SAVE_AUTH](state: AuthState, payload: LoginResponse): void {
    state.token = payload.token
    localStorageSet('token', payload.token)
    let expiresTimestamp = +new Date()
    expiresTimestamp += payload.expires * 1000
    localStorageSet('expires', '' + expiresTimestamp)
    state.expires = expiresTimestamp
  },

  /**
   * Persist auth email
   *
   * @param {AuthState} state
   * @param {string} email
   */
  [_AUTH__SET_EMAIL](state: AuthState, email: string): void {
    state.email = email
    localStorageSet('email', email)
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
